package com.shadowings.gero.presentation.component.quiz

import androidx.compose.foundation.BorderStroke
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.heightIn
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.width
import androidx.compose.foundation.pager.PagerState
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material3.CardDefaults
import androidx.compose.material3.Checkbox
import androidx.compose.material3.CheckboxDefaults
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.OutlinedCard
import androidx.compose.material3.Surface
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.MutableState
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.saveable.rememberSaveable
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.drawWithContent
import androidx.compose.ui.graphics.rememberGraphicsLayer
import androidx.compose.ui.hapticfeedback.HapticFeedbackType
import androidx.compose.ui.platform.LocalHapticFeedback
import androidx.compose.ui.semantics.semantics
import androidx.compose.ui.semantics.testTag
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.text.style.TextAlign
import androidx.compose.ui.tooling.preview.Preview
import androidx.compose.ui.unit.dp
import androidx.navigation.NavController
import androidx.navigation.compose.rememberNavController
import com.shadowings.gero.domain.model.IQuizContent
import com.shadowings.gero.domain.model.LessonIds
import com.shadowings.gero.domain.model.MultiSelectionContent
import com.shadowings.gero.domain.model.Option
import com.shadowings.gero.domain.model.QuizText
import com.shadowings.gero.domain.model.UserData
import com.shadowings.gero.presentation.component.MdCWord
import com.shadowings.gero.presentation.component.looksLikeGardinerCode
import com.shadowings.gero.presentation.model.HeartActions
import com.shadowings.gero.presentation.model.QuizUiState
import com.shadowings.gero.presentation.screen.QuizScreenContent
import com.shadowings.gero.presentation.ui.AppTheme
import com.shadowings.gero.presentation.ui.correctBorder
import com.shadowings.gero.presentation.ui.quizSelectionCorrectBg
import com.shadowings.gero.presentation.ui.quizSelectionPendingBg
import com.shadowings.gero.presentation.ui.quizSelectionWrongBg
import com.shadowings.gero.presentation.ui.testModeCorrect
import com.shadowings.gero.presentation.ui.wrongBorder
import com.shadowings.gero.presentation.util.DeviceClass
import com.shadowings.gero.presentation.util.DevicePreviews
import com.shadowings.gero.presentation.util.rememberDeviceInfo
import gero.composeapp.generated.resources.Res
import gero.composeapp.generated.resources.share_quiz_multi
import org.jetbrains.compose.resources.stringResource

@DevicePreviews
@Preview
@Preview(name = "Dark", uiMode = 0x20, showBackground = true)
@Preview(name = "Small Screen (Low Height)", heightDp = 480, widthDp = 320, showBackground = true)
@Preview(name = "Landscape", widthDp = 800, heightDp = 360, showBackground = true)
@Composable
private fun MultiSelectionComposablePreview() {
    AppTheme {
        Surface {
            QuizScreenContent(
                uiState = QuizUiState(
                    lessonId = LessonIds.INTRO_HIEROGLYPHS,
                    quizList = listOf(
                        MultiSelectionContent(
                            id = "preview",
                            text = QuizText.Plain("Quale di questi è un geroglifico?"),
                            options = listOf(
                                Option("𓂀 Occhio di Ra", correct = true),
                                Option("ABC", correct = false),
                                Option("123", correct = false),
                                Option(
                                    "Lorem ipsum sit amet Lorem ipsum sit amet Lorem ipsum sit amet Lorem ipsum sit amet Lorem ipsum sit amet ",
                                    correct = false
                                )
                            )
                        )
                    ),
                    userData = UserData()
                ),
                hiero = listOf(),
                onQuizCompleted = { _, _, _ -> },
                onAddExperience = {},
                heartActions = HeartActions({}),
                navController = rememberNavController()
            )
        }
    }
}


@Composable
fun MultiSelectionComposable(
    quiz: MultiSelectionContent,
    comboCount: MutableState<Int>,
    quizList: MutableState<List<IQuizContent>>,
    page: Int,
    pagerState: PagerState,
    navController: NavController,
    quizResults: MutableState<Map<String, Boolean>>,
    errorsCount: MutableState<Int>,
    testMode: Boolean,
    userData: UserData,
    lessonId: String = "",
    onComboFiveReached: (() -> Unit)? = null,
    onNextQuestion: ((isCorrect: Boolean) -> Unit)? = null,
    // Deliberately no onAnswerResult param (see onConfirmClick below): MULTI_SELECTION mistakes
    // are not recorded into the mistakes list.
) {
    val state = rememberQuizPageState(
        quizId = quiz.id,
        page = page,
        pagerState = pagerState,
        navController = navController,
        quizResults = quizResults,
        quizList = quizList,
        comboCount = comboCount,
        errorsCount = errorsCount,
        onComboFiveReached = onComboFiveReached,
        onNextQuestion = onNextQuestion,
    )
    val graphicsLayer = rememberGraphicsLayer()
    val hapticFeedback = LocalHapticFeedback.current
    val deviceInfo = rememberDeviceInfo()

    var selections by rememberSaveable { mutableStateOf(BooleanArray(quiz.options.size)) }

    val isLandscape = deviceInfo.isLandscape

    Box(modifier = Modifier.fillMaxSize()) {
        run {
            val questionContent = @Composable {
                val textString = quiz.text.resolve()
                val parts = textString.split("::")
                if (parts.size == 2) {
                    QuestionBox(
                        question = parts[0],
                        questionTestTag = "multi_selection_question",
                        wordContent = {
                            Box(
                                modifier = Modifier.drawWithContent {
                                    graphicsLayer.record {
                                        this@drawWithContent.drawContent()
                                    }
                                    drawContent()
                                },
                                contentAlignment = Alignment.Center
                            ) {
                                MdCWord(
                                    word = parts[1],
                                    rtl = false,
                                    cart = false,
                                    size = 64.dp,
                                    testTag = parts[1]
                                )
                            }
                        }
                    )
                } else {
                    QuestionBox(
                        question = textString,
                        questionTestTag = "multi_selection_question"
                    )
                }
            }

            val optionsContent = @Composable {
                quiz.options.forEachIndexed { index, option ->
                    val optionText = option.text.resolve()
                    val selected = selections[index]
                    val bg = when {
                        state.blinking -> MaterialTheme.colorScheme.surfaceVariant
                        selected && !state.showResult -> quizSelectionPendingBg
                        state.showResult && option.correct -> quizSelectionCorrectBg
                        selected && state.showResult && !option.correct -> quizSelectionWrongBg
                        else -> MaterialTheme.colorScheme.surfaceVariant
                    }

                    val isGardiner = looksLikeGardinerCode(optionText)

                    if (isGardiner) {
                        OutlinedCard(
                            onClick = {
                                if (state.pending) {
                                    hapticFeedback.performHapticFeedback(HapticFeedbackType.LongPress)
                                    selections = BooleanArray(quiz.options.size) { i ->
                                        if (i == index) !selections[i] else selections[i]
                                    }
                                }
                            },
                            enabled = !state.showResult,
                            shape = RoundedCornerShape(12.dp),
                            colors = CardDefaults.outlinedCardColors(
                                containerColor = bg,
                                contentColor = MaterialTheme.colorScheme.onSurfaceVariant,
                                disabledContainerColor = bg,
                                disabledContentColor = MaterialTheme.colorScheme.onSurfaceVariant
                            ),
                            border = BorderStroke(
                                width = if (selected && !state.showResult) 2.dp else 1.dp,
                                color = if (selected && !state.showResult) {
                                    MaterialTheme.colorScheme.primary
                                } else if (testMode && option.correct) {
                                    testModeCorrect
                                } else {
                                    MaterialTheme.colorScheme.outline.copy(alpha = 0.5f)
                                }
                            ),
                            modifier = Modifier
                                .padding(horizontal = 8.dp)
                                .fillMaxWidth()
                                .semantics { testTag = optionText }
                        ) {
                            Row(
                                modifier = Modifier
                                    .fillMaxWidth()
                                    .heightIn(min = if (deviceInfo.deviceClass == DeviceClass.PHONE_SMALL) 48.dp else 64.dp)
                                    .padding(horizontal = 16.dp, vertical = 8.dp),
                                verticalAlignment = Alignment.CenterVertically
                            ) {
                                Checkbox(
                                    checked = selected,
                                    onCheckedChange = null,
                                    colors = CheckboxDefaults.colors(
                                        checkedColor = if (state.showResult) {
                                            if (option.correct) correctBorder else wrongBorder
                                        } else {
                                            MaterialTheme.colorScheme.primary
                                        },
                                        uncheckedColor = if (state.showResult) {
                                            if (option.correct) correctBorder else MaterialTheme.colorScheme.outline
                                        } else {
                                            MaterialTheme.colorScheme.outline
                                        }
                                    )
                                )
                                Spacer(modifier = Modifier.width(8.dp))
                                Box(
                                    modifier = Modifier.weight(1f),
                                    contentAlignment = Alignment.Center
                                ) {
                                    MdCWord(
                                        word = optionText,
                                        rtl = false,
                                        cart = false,
                                        size = 48.dp,
                                        testTag = optionText
                                    )
                                }
                                Spacer(modifier = Modifier.width(24.dp))
                            }
                        }
                    } else {
                        OutlinedCard(
                            onClick = {
                                if (state.pending) {
                                    hapticFeedback.performHapticFeedback(HapticFeedbackType.LongPress)
                                    selections = BooleanArray(quiz.options.size) { i ->
                                        if (i == index) !selections[i] else selections[i]
                                    }
                                }
                            },
                            enabled = !state.showResult,
                            shape = RoundedCornerShape(12.dp),
                            colors = CardDefaults.outlinedCardColors(
                                containerColor = bg,
                                contentColor = MaterialTheme.colorScheme.onSurfaceVariant,
                                disabledContainerColor = bg,
                                disabledContentColor = MaterialTheme.colorScheme.onSurfaceVariant
                            ),
                            border = BorderStroke(
                                width = if (selected && !state.showResult) 2.dp else 1.dp,
                                color = if (selected && !state.showResult) {
                                    MaterialTheme.colorScheme.primary
                                } else if (testMode && option.correct) {
                                    testModeCorrect
                                } else {
                                    MaterialTheme.colorScheme.outline.copy(alpha = 0.5f)
                                }
                            ),
                            modifier = Modifier
                                .padding(horizontal = 8.dp)
                                .fillMaxWidth()
                                .semantics { testTag = optionText }
                        ) {
                            Row(
                                modifier = Modifier
                                    .fillMaxWidth()
                                    .heightIn(min = if (deviceInfo.deviceClass == DeviceClass.PHONE_SMALL) 48.dp else 64.dp)
                                    .padding(horizontal = 16.dp, vertical = 8.dp),
                                verticalAlignment = Alignment.CenterVertically
                            ) {
                                Checkbox(
                                    checked = selected,
                                    onCheckedChange = null,
                                    colors = CheckboxDefaults.colors(
                                        checkedColor = if (state.showResult) {
                                            if (option.correct) correctBorder else wrongBorder
                                        } else {
                                            MaterialTheme.colorScheme.primary
                                        },
                                        uncheckedColor = if (state.showResult) {
                                            if (option.correct) correctBorder else MaterialTheme.colorScheme.outline
                                        } else {
                                            MaterialTheme.colorScheme.outline
                                        }
                                    )
                                )
                                Spacer(modifier = Modifier.width(8.dp))
                                Box(
                                    modifier = Modifier.weight(1f),
                                    contentAlignment = Alignment.Center
                                ) {
                                    Text(
                                        text = optionText,
                                        style = MaterialTheme.typography.bodyMedium,
                                        fontWeight = FontWeight.Bold,
                                        textAlign = TextAlign.Center
                                    )
                                }
                                Spacer(modifier = Modifier.width(24.dp))
                            }
                        }
                    }
                }
            }

            QuizContentScaffold(
                testTag = "true_false_content",
                isLandscape = isLandscape,
                questionContent = { questionContent() },
                optionsContent = { optionsContent() }
            )
        }
        val displayCorrectAnswer = quiz.options
            .mapIndexed { index, option -> if (option.correct) (index + 1).toString() else null }
            .filterNotNull()
            .joinToString(", ")
        val quizInfo = """
            Quiz ID: ${quiz.id}
            Type: Multi Selection Quiz
            Question: ${quiz.text.resolve()}
            Options:
            ${
            quiz.options.map { "${it.text.resolve()} (Correct: ${it.correct})" }
                .joinToString("\n            ")
        }
        """.trimIndent()
        val userAnswer = quiz.options.mapIndexedNotNull { index, option ->
            if (selections.getOrElse(index) { false }) option.text.resolve() else null
        }.joinToString(", ").ifBlank { "None" }

        val resolvedQuestion = quiz.text.resolve()
        val parts = resolvedQuestion.split("::")
        val shareText = stringResource(Res.string.share_quiz_multi, parts[0])

        QuizBottomFeedbackAndButton(
            modifier = Modifier.align(Alignment.BottomCenter),
            state = state,
            buttonEnabled = selections.any { it },
            correctAnswer = displayCorrectAnswer,
            userData = userData,
            lessonId = lessonId,
            quizInfo = quizInfo,
            userAnswer = userAnswer,
            shareText = shareText,
            shareImageProvider = if (parts.size == 2) {
                {
                    try {
                        graphicsLayer.toImageBitmap()
                    } catch (e: Exception) {
                        null
                    }
                }
            } else {
                null
            },
            onConfirmClick = {
                val isCorrect = selections.mapIndexed { index, bool ->
                    bool == quiz.options[index].correct
                }.all { it }
                // Not recorded into the mistakes list: this quiz shape (several independently
                // toggled options, each individually right/wrong) doesn't reduce to a single
                // "correct answer" string the way the other types do, so a saved mistake can't be
                // rebuilt into a faithful correction question later - and there are only a
                // handful of MULTI_SELECTION questions in the whole app, not worth the special
                // case. onAnswerResult is deliberately not invoked here.
                isCorrect
            }
        )
    }
}
